import React, { useEffect, useState, useRef, useCallback } from 'react'
import { View, Text, StyleSheet, ActivityIndicator, Platform } from 'react-native'
import { MapPin } from 'lucide-react-native'
import theme from '../theme'
import { useLanguage } from '@fliisi/i18n'

// Conditionally import Leaflet for web
let MapContainer: any = null
let TileLayer: any = null
let Marker: any = null
let useMap: any = null
let L: any = null

if (Platform.OS === 'web') {
  try {
    const ReactLeaflet = require('react-leaflet')
    MapContainer = ReactLeaflet.MapContainer
    TileLayer = ReactLeaflet.TileLayer
    Marker = ReactLeaflet.Marker
    useMap = ReactLeaflet.useMap

    // Load leaflet CSS from CDN (local css url() imports not supported by Metro)
    if (!document.querySelector('link[href*="leaflet"]')) {
      const link = document.createElement('link')
      link.rel = 'stylesheet'
      link.href = 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.css'
      document.head.appendChild(link)
    }
    L = require('leaflet')

    // Fix default marker icons
    delete (L.Icon.Default.prototype as any)._getIconUrl
    L.Icon.Default.mergeOptions({
      iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
      iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
      shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
    })

    // Ensure leaflet container fills its parent
    const style = document.createElement('style')
    style.textContent = `
      .location-picker-map .leaflet-container {
        width: 100% !important;
        height: 100% !important;
        border-radius: 12px;
      }
    `
    document.head.appendChild(style)
  } catch (e) {
    console.warn('Leaflet not available for LocationPicker:', e)
  }
}

interface LocationPickerProps {
  address: string
  city: string
  initialLat?: number | null
  initialLng?: number | null
  onLocationChange: (lat: number, lng: number) => void
}

// Default center: Helsinki
const DEFAULT_CENTER: [number, number] = [60.1699, 24.9384]
const DEFAULT_ZOOM = 16

// Known Finnish city centers for instant fallback
const CITY_COORDS: Record<string, [number, number]> = {
  'helsinki': [60.1699, 24.9384],
  'espoo': [60.2055, 24.6559],
  'tampere': [61.4978, 23.7610],
  'vantaa': [60.2934, 25.0378],
  'oulu': [65.0121, 25.4651],
  'turku': [60.4518, 22.2666],
  'jyväskylä': [62.2426, 25.7473],
  'lahti': [60.9827, 25.6612],
  'kuopio': [62.8924, 27.6770],
  'pori': [61.4851, 21.7974],
  'joensuu': [62.6010, 29.7636],
  'lappeenranta': [61.0587, 28.1887],
  'hämeenlinna': [60.9966, 24.4641],
  'vaasa': [63.0960, 21.6158],
  'seinäjoki': [62.7876, 22.8402],
  'rovaniemi': [66.5039, 25.7294],
  'mikkeli': [61.6886, 27.2723],
  'kotka': [60.4664, 26.9458],
  'salo': [60.3831, 23.1270],
  'porvoo': [60.3929, 25.6649],
}

// Geocode via Digitransit (Finnish national geocoder), fallback to Nominatim
async function geocodeAddress(address: string, city: string): Promise<{ lat: number; lng: number } | null> {
  const fullAddress = [address, city, 'Finland'].filter(Boolean).join(', ')
  if (fullAddress.replace(/,\s*/g, '').length < 5) return null

  // Try Digitransit first
  try {
    const res = await fetch(
      `https://api.digitransit.fi/geocoding/v1/search?text=${encodeURIComponent(fullAddress)}&size=1&lang=fi`,
      { headers: { 'Content-Type': 'application/json' } }
    )
    if (res.ok) {
      const data = await res.json()
      const feature = data?.features?.[0]
      if (feature?.geometry?.coordinates) {
        const [lng, lat] = feature.geometry.coordinates
        return { lat, lng }
      }
    }
  } catch (e) {
    console.warn('Digitransit geocoding failed:', e)
  }

  // Fallback: Nominatim
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(fullAddress)}&limit=1`,
      { headers: { 'User-Agent': 'Fliisi-App/1.0' } }
    )
    if (res.ok) {
      const data = await res.json()
      if (data?.[0]) {
        return { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) }
      }
    }
  } catch (e) {
    console.warn('Nominatim geocoding failed:', e)
  }

  return null
}

// Inner component that has access to the map instance
function DraggableMarker({ position, onDragEnd }: { position: [number, number]; onDragEnd: (lat: number, lng: number) => void }) {
  const markerRef = useRef<any>(null)

  const eventHandlers = {
    dragend() {
      const marker = markerRef.current
      if (marker) {
        const latlng = marker.getLatLng()
        onDragEnd(latlng.lat, latlng.lng)
      }
    },
  }

  return (
    <Marker
      draggable={true}
      eventHandlers={eventHandlers}
      position={position}
      ref={markerRef}
    />
  )
}

// Component that updates the map view when center changes
function MapUpdater({ center }: { center: [number, number] }) {
  const map = useMap?.()
  useEffect(() => {
    if (map && center) {
      map.flyTo(center, map.getZoom(), { duration: 0.5 })
    }
  }, [map, center[0], center[1]])
  return null
}

export default function LocationPicker({
  address,
  city,
  initialLat,
  initialLng,
  onLocationChange,
}: LocationPickerProps) {
  const { language } = useLanguage()
  const [geocoding, setGeocoding] = useState(false)
  const [markerPos, setMarkerPos] = useState<[number, number]>(
    initialLat && initialLng ? [initialLat, initialLng] : DEFAULT_CENTER
  )
  const [mapCenter, setMapCenter] = useState<[number, number]>(
    initialLat && initialLng ? [initialLat, initialLng] : DEFAULT_CENTER
  )
  const [geocodeFailed, setGeocodeFailed] = useState(false)
  const debounceRef = useRef<any>(null)
  const hasGeocodedRef = useRef(false)

  // If we have initial coordinates, use them
  useEffect(() => {
    if (initialLat && initialLng) {
      setMarkerPos([initialLat, initialLng])
      setMapCenter([initialLat, initialLng])
      hasGeocodedRef.current = true
    }
  }, [initialLat, initialLng])

  // Geocode when address or city changes
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)

    // Don't auto-geocode if we already have initial coords and nothing changed
    if (hasGeocodedRef.current && !address && !city) return

    debounceRef.current = setTimeout(async () => {
      if (!address && !city) return

      setGeocoding(true)
      setGeocodeFailed(false)

      const coords = await geocodeAddress(address, city)

      if (coords) {
        const pos: [number, number] = [coords.lat, coords.lng]
        setMarkerPos(pos)
        setMapCenter(pos)
        onLocationChange(coords.lat, coords.lng)
        hasGeocodedRef.current = true
      } else {
        // Fall back to city center
        const cityLower = city?.toLowerCase()
        if (cityLower && CITY_COORDS[cityLower]) {
          const pos = CITY_COORDS[cityLower]
          setMarkerPos(pos)
          setMapCenter(pos)
          onLocationChange(pos[0], pos[1])
        }
        setGeocodeFailed(true)
      }

      setGeocoding(false)
    }, 1000)

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [address, city])

  const handleDragEnd = useCallback((lat: number, lng: number) => {
    setMarkerPos([lat, lng])
    onLocationChange(lat, lng)
  }, [onLocationChange])

  // Only render on web with Leaflet available
  if (Platform.OS !== 'web' || !MapContainer) {
    return null
  }

  return (
    <View style={styles.container}>
      <View style={styles.labelRow}>
        <MapPin size={16} color={theme.colors.text.secondary} />
        <Text style={styles.label}>
          {language === 'fi' ? 'Sijainti kartalla' : 'Location on map'}
        </Text>
        {geocoding && <ActivityIndicator size="small" color={theme.colors.brand.primary} style={styles.spinner} />}
      </View>

      <View style={styles.mapContainer} className="location-picker-map">
        <MapContainer
          center={mapCenter}
          zoom={DEFAULT_ZOOM}
          style={{ width: '100%', height: '100%' }}
          scrollWheelZoom={true}
          zoomControl={true}
        >
          <MapUpdater center={mapCenter} />
          <TileLayer
            attribution='&copy; <a href="https://carto.com/">CARTO</a>'
            url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
          />
          <DraggableMarker position={markerPos} onDragEnd={handleDragEnd} />
        </MapContainer>
      </View>

      <Text style={styles.hint}>
        {geocodeFailed
          ? (language === 'fi'
            ? '⚠️ Osoitetta ei löytynyt — aseta sijainti vetämällä merkkiä'
            : "⚠️ Couldn't find address — drag the marker to set location")
          : (language === 'fi'
            ? '📍 Vedä merkkiä säätääksesi sijaintia'
            : '📍 Drag marker to adjust location')}
      </Text>
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    marginBottom: 16,
  },
  labelRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
    gap: 6,
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
    color: theme.colors.text.primary,
  },
  spinner: {
    marginLeft: 8,
  },
  mapContainer: {
    height: 200,
    borderRadius: 12,
    overflow: 'hidden',
    borderWidth: 2,
    borderColor: theme.colors.ui.border,
  },
  hint: {
    fontSize: 12,
    color: theme.colors.text.secondary,
    marginTop: 6,
  },
})
